import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/Common";
import ProductHoldings from "@/components/ProductHoldings";
import { ConfirmButton } from "@/components/ConfirmDialog";
import { formatINR, POTENTIAL_LABELS, initials } from "@/lib/format";
import {
  ArrowLeft, Phone, Mail, MapPin, Briefcase, CreditCard, Flame, Users2,
  PhoneCall, CalendarClock, StickyNote, MessageCircle, Plus, ArrowRightLeft,
  TrendingUp, TrendingDown, Wallet, Shield, Pencil, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ACTIVITY_ICONS = { call: PhoneCall, meeting: Users2, followup: CalendarClock, note: StickyNote, whatsapp: MessageCircle };

function formatBirthday(dob) {
  const d = new Date(String(dob) + "T00:00:00");
  if (isNaN(d.getTime())) return dob;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export default function ClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [products, setProducts] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [activities, setActivities] = useState([]);
  const [managers, setManagers] = useState([]);
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    const [c, p, h, a] = await Promise.all([
      api.get(`/clients/${id}`), api.get("/products"),
      api.get(`/clients/${id}/holdings`), api.get(`/clients/${id}/activities`),
    ]);
    setClient(c.data); setProducts(p.data); setHoldings(h.data); setActivities(a.data);
  }, [id]);

  useEffect(() => {
    load();
    if (user.role !== "manager") api.get("/managers").then((r) => setManagers(r.data));
  }, [id, load]);

  const remove = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted");
      navigate("/clients");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!client) return <div className="text-muted-foreground py-20 text-center">Loading client…</div>;
  const pf = client.portfolio || {};
  const filteredActivities = tab === "all" ? activities : activities.filter((a) => a.type === tab);

  return (
    <div className="animate-fade-up space-y-6">
      <button onClick={() => navigate("/clients")} data-testid="back-button" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground row-hover">
        <ArrowLeft className="h-4 w-4" /> Back to Clients
      </button>

      {/* Header */}
      <div className="card-surface rounded-xl p-6" data-testid="client-header">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-2xl bg-primary/15 text-primary flex items-center justify-center text-xl font-bold num">{initials(client.name)}</div>
            <div>
              <h1 className="font-display text-2xl font-bold">{client.name}</h1>
              <div className="text-sm text-muted-foreground">Family Head: {client.family_head || "—"}</div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <StarRating value={client.potential} size={16} />
                <span className="text-xs text-gold font-medium">{POTENTIAL_LABELS[client.potential]} Potential</span>
                {client.dob && (
                  <span data-testid="client-birthday-badge" className="inline-flex items-center gap-1.5 text-xs font-medium bg-gold/15 text-gold rounded-full px-2.5 py-0.5">
                    🎂 {formatBirthday(client.dob)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <EditClientDialog client={client} onDone={load} />
            {user.role !== "manager" && <TransferDialog clientId={id} managers={managers} onDone={load} />}
            <ConfirmButton
              title="Delete client?"
              description={`This permanently deletes "${client.name}" and all their holdings, activities and notes.`}
              confirmLabel="Delete Client"
              testid="client-delete-confirm"
              onConfirm={remove}
              trigger={
                <button data-testid="client-delete-button" className="flex items-center gap-2 rounded-lg border border-destructive/40 text-destructive px-3.5 py-2 text-sm font-medium hover:bg-destructive/10 row-hover">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              }
            />
          </div>
        </div>
        <div className="mt-5 pt-5 border-t border-border/60 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Info icon={Phone} label="Mobile" value={client.mobile} />
          <Info icon={Mail} label="Email" value={client.email} />
          <Info icon={Briefcase} label="Occupation" value={client.occupation} />
          <Info icon={CreditCard} label="PAN" value={client.pan} />
          <Info icon={MapPin} label="Location" value={[client.city, client.state].filter(Boolean).join(", ")} />
          <Info icon={CreditCard} label="UCC Code" value={client.ucc_code} />
          <Info icon={Users2} label="Manager" value={client.manager_name} />
          <Info icon={MapPin} label="Branch" value={client.branch_name} />
        </div>
      </div>

      {/* Portfolio summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="portfolio-summary">
        <Metric label="Total Invested" value={formatINR(pf.total_invested)} icon={Wallet} />
        <Metric label="Current Value" value={formatINR(pf.total_current)} icon={TrendingUp} accent="primary" />
        <Metric label="Gain / Loss" value={formatINR(pf.total_gain_loss)} icon={TrendingUp} accent={pf.total_gain_loss >= 0 ? "primary" : "danger"} />
        <Metric label="Liabilities" value={formatINR(pf.total_liabilities)} icon={TrendingDown} accent="danger" />
        <Metric label="Net Worth" value={formatINR(pf.net_worth)} icon={Wallet} accent="gold" />
      </div>
      {(pf.total_sum_assured > 0 || pf.total_premium > 0) && (
        <div className="flex flex-wrap gap-4">
          <div className="card-surface rounded-xl px-4 py-3 flex items-center gap-3">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-xs text-muted-foreground">Sum Assured</span>
            <span className="num text-sm font-semibold">{formatINR(pf.total_sum_assured)}</span>
          </div>
          <div className="card-surface rounded-xl px-4 py-3 flex items-center gap-3">
            <Shield className="h-4 w-4 text-gold" />
            <span className="text-xs text-muted-foreground">Total Premium</span>
            <span className="num text-sm font-semibold">{formatINR(pf.total_premium)}</span>
          </div>
        </div>
      )}

      {/* Product-wise contribution */}
      {pf.per_product?.length > 0 && (
        <div className="card-surface rounded-xl p-5" data-testid="product-contribution">
          <h3 className="font-display font-semibold mb-4">Portfolio Allocation</h3>
          <div className="space-y-2">
            {pf.per_product.map((p) => {
              const max = Math.max(...pf.per_product.map((x) => x.current), 1);
              return (
                <div key={p.key} className="flex items-center gap-3">
                  <div className="w-32 text-sm truncate shrink-0">{p.label}</div>
                  <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${(p.current / max) * 100}%` }} />
                  </div>
                  <div className="w-24 text-right num text-sm font-medium">{formatINR(p.current)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ProductHoldings client={client} products={products} holdings={holdings} onChange={load} />
        </div>

        {/* Cross-sell */}
        <div className="card-surface rounded-xl p-5 h-fit">
          <div className="flex items-center gap-2 mb-4">
            <Flame className="h-4 w-4 text-gold" />
            <h3 className="font-display font-semibold">Cross-Sell Opportunities</h3>
          </div>
          <div className="space-y-3" data-testid="crosssell-cards">
            {client.cross_sell.length === 0 && <div className="text-sm text-muted-foreground">No open opportunities — well diversified!</div>}
            {client.cross_sell.map((o) => (
              <CrossSellCard key={o.key} clientId={id} opp={o} onDone={load} />
            ))}
          </div>
        </div>
      </div>

      {/* Activity */}
      <div className="card-surface rounded-xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="font-display font-semibold">Activity & Notes</h3>
          <LogActivityDialog clientId={id} onDone={load} />
        </div>
        <div className="flex gap-1.5 mb-4 flex-wrap">
          {["all", "call", "meeting", "followup", "note", "whatsapp"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`activity-tab-${t}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize row-hover ${tab === t ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="space-y-3" data-testid="activity-timeline">
          {filteredActivities.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No activities logged yet.</div>}
          {filteredActivities.map((a) => {
            const Icon = ACTIVITY_ICONS[a.type] || StickyNote;
            return (
              <div key={a.id} className="flex gap-3">
                <div className="h-9 w-9 rounded-full bg-secondary text-primary flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
                <div className="flex-1 min-w-0 pb-3 border-b border-border/40">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium capitalize">{a.type}{a.follow_up_date && <span className="ml-2 text-xs text-gold">Due {new Date(a.follow_up_date).toLocaleDateString("en-IN")}</span>}</span>
                    <span className="text-xs text-muted-foreground num">{new Date(a.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">{a.note}</div>
                  <div className="text-xs text-muted-foreground mt-1">by {a.created_by_name}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Icon className="h-3 w-3" /> {label}</div>
      <div className="text-sm mt-0.5 truncate">{value || "—"}</div>
    </div>
  );
}

function Metric({ label, value, icon: Icon, accent }) {
  const cls = accent === "gold" ? "text-gold" : accent === "danger" ? "text-red-400" : accent === "primary" ? "text-primary" : "text-foreground";
  return (
    <div className="card-surface rounded-xl p-4 stat-glow">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">{label}</div>
        <Icon className={`h-4 w-4 ${cls}`} />
      </div>
      <div className={`mt-2 font-display text-xl font-bold num ${cls}`}>{value}</div>
    </div>
  );
}

function CrossSellCard({ clientId, opp, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [date, setDate] = useState("");

  const act = async (action, extra = {}) => {
    try {
      await api.post(`/clients/${clientId}/opportunities`, { product_key: opp.key, action, ...extra });
      toast.success("Updated");
      setOpen(false); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className={`rounded-lg border p-3.5 ${opp.status === "interested" ? "border-primary/40 bg-primary/5" : "border-gold/30 bg-gold/5"}`} data-testid={`crosssell-${opp.key}`}>
      <div className="flex items-center justify-between">
        <span className="font-medium text-sm text-gold">🔥 {opp.product}</span>
        <span className="text-xs num text-muted-foreground">{opp.score}%</span>
      </div>
      <div className="text-xs text-muted-foreground mt-1">{opp.reason}</div>
      {opp.status === "interested" && <div className="text-xs text-primary mt-1 font-medium">✓ Interested</div>}
      <div className="flex gap-1.5 mt-2 flex-wrap">
        <button onClick={() => act("interested")} data-testid={`opp-interested-${opp.key}`} className="text-xs px-2 py-1 rounded-md bg-primary/15 text-primary hover:bg-primary/25 row-hover">Interested</button>
        <button onClick={() => act("not_interested")} data-testid={`opp-notinterested-${opp.key}`} className="text-xs px-2 py-1 rounded-md bg-secondary text-muted-foreground hover:text-foreground row-hover">Not Interested</button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid={`opp-followup-${opp.key}`} className="text-xs px-2 py-1 rounded-md bg-gold/15 text-gold hover:bg-gold/25 row-hover">Schedule</button>
          </DialogTrigger>
          <DialogContent className="card-surface">
            <DialogHeader><DialogTitle>Schedule Follow-up · {opp.product}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Follow-up Date</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="opp-followup-date" className="mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary" />
              </div>
              <div>
                <label className="text-sm font-medium">Note</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="opp-followup-note" rows={2} className="mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary resize-none" />
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => date ? act("followup", { follow_up_date: date, note }) : toast.error("Pick a date")} data-testid="opp-followup-save" className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">Save Follow-up</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function LogActivityDialog({ clientId, onDone }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("call");
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");

  const save = async () => {
    if (!note.trim()) return toast.error("Note is required");
    try {
      await api.post(`/clients/${clientId}/activities`, { type, note, follow_up_date: followUp || null });
      toast.success("Activity logged");
      setOpen(false); setNote(""); setType("call"); setFollowUp(""); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="log-activity-button" className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3.5 py-1.5 text-sm font-semibold hover:bg-blue-500 transition-colors">
          <Plus className="h-4 w-4" /> Log Activity
        </button>
      </DialogTrigger>
      <DialogContent className="card-surface">
        <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="activity-type-select" className="mt-1 bg-secondary border-input"><SelectValue /></SelectTrigger>
              <SelectContent>{["call", "meeting", "followup", "note", "whatsapp"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Note</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="activity-note-input" rows={3} className="mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary transition-colors resize-none" placeholder="What happened…" />
          </div>
          <div>
            <label className="text-sm font-medium">Follow-up Date (optional)</label>
            <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="activity-followup-input" className="mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="activity-save-button" onClick={save} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">Save Activity</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditClientDialog({ client, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => { if (open) setForm({ ...client }); }, [open, client]);

  const save = async () => {
    try {
      const keys = ["name", "family_head", "dob", "gender", "occupation", "pan", "ucc_code",
        "email", "mobile", "address", "city", "state", "pincode", "potential"];
      const payload = {};
      keys.forEach((k) => { if (form[k] !== undefined) payload[k] = form[k]; });
      await api.put(`/clients/${client.id}`, payload);
      toast.success("Client updated");
      setOpen(false); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const field = (label, k, type = "text") => (
    <div key={k}>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <input type={type} value={form[k] ?? ""} data-testid={`edit-client-${k}`}
        onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))}
        className="mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary transition-colors" />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="edit-client-button" className="flex items-center gap-2 rounded-lg border border-border px-3.5 py-2 text-sm font-medium hover:border-primary/40 row-hover">
          <Pencil className="h-4 w-4" /> Edit
        </button>
      </DialogTrigger>
      <DialogContent className="card-surface max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Client</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {field("Name", "name")}
          {field("Family Head", "family_head")}
          {field("Date of Birth", "dob", "date")}
          {field("Occupation", "occupation")}
          {field("PAN", "pan")}
          {field("UCC Code", "ucc_code")}
          {field("Email", "email")}
          {field("Mobile", "mobile")}
          {field("City", "city")}
          {field("State", "state")}
          {field("Pincode", "pincode")}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Potential</label>
            <div className="mt-2"><StarRating value={form.potential || 3} size={22} onChange={(v) => setForm((s) => ({ ...s, potential: v }))} /></div>
          </div>
        </div>
        <DialogFooter>
          <button data-testid="edit-client-save" onClick={save} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">Save Changes</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ clientId, managers, onDone }) {
  const [open, setOpen] = useState(false);
  const [managerId, setManagerId] = useState("");
  const transfer = async () => {
    if (!managerId) return toast.error("Select a manager");
    try {
      await api.post(`/clients/${clientId}/transfer`, { manager_id: managerId });
      toast.success("Client transferred");
      setOpen(false); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="transfer-client-button" className="flex items-center gap-2 rounded-lg border border-border px-3.5 py-2 text-sm font-medium hover:border-primary/40 row-hover">
          <ArrowRightLeft className="h-4 w-4" /> Transfer
        </button>
      </DialogTrigger>
      <DialogContent className="card-surface">
        <DialogHeader><DialogTitle>Transfer Client</DialogTitle></DialogHeader>
        <div>
          <label className="text-sm font-medium">New Manager</label>
          <Select value={managerId} onValueChange={setManagerId}>
            <SelectTrigger data-testid="transfer-manager-select" className="mt-1 bg-secondary border-input"><SelectValue placeholder="Select manager" /></SelectTrigger>
            <SelectContent>{managers.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`transfer-option-${m.id}`}>{m.name} · {m.branch_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <button data-testid="transfer-confirm-button" onClick={transfer} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">Confirm Transfer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
